'use client';

import { RefreshCw, ServerCrash } from 'lucide-react';
import { Figtree, Geist, Geist_Mono } from 'next/font/google';
import { NextIntlClientProvider, useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { MessageDisplay, MessageDisplayButton } from '@/components/message-display';
import { getLocaleFromCookieSync, getMessagesSync } from '@/i18n/get-messages-sync';
import { rollbar } from '@/lib/rollbar';

import './globals.css';

const figtree = Figtree({ subsets: ['latin'], variable: '--font-sans' });

const geistSans = Geist({
  variable: '--font-geist-sans',
  subsets: ['latin'],
});

const geistMono = Geist_Mono({
  variable: '--font-geist-mono',
  subsets: ['latin'],
});

type GlobalErrorProps = {
  error: Error;
  reset: () => void;
};

type GlobalErrorContentProps = Pick<GlobalErrorProps, 'reset'>;

const GlobalErrorContent: React.FC<GlobalErrorContentProps> = ({ reset }) => {
  const t = useTranslations('app.globalError');

  return (
    <MessageDisplay
      intent="error"
      size="page"
      icon={ServerCrash}
      title={t('title')}
      description={t('description')}
      className="h-screen"
    >
      <MessageDisplayButton onClick={reset}>
        <RefreshCw className="size-4" />
        {t('retry')}
      </MessageDisplayButton>
    </MessageDisplay>
  );
};

const GlobalErrorPage: React.FC<GlobalErrorProps> = ({ error, reset }) => {
  useEffect(() => {
    // Root layout and template are not available in the error page
    // so we don't have the RollbarProvider available to use the
    // useRollbar hook so we need to create a new Rollbar instance here
    rollbar.error('unhandled error caught at GlobalError boundary', error);
  }, [error]);

  const locale = getLocaleFromCookieSync();
  const messages = getMessagesSync(locale);

  return (
    <html lang={locale} className={figtree.variable}>
      <head>
        <link rel="icon" href="/icon.svg" sizes="any" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/icons/ios/apple-touch-icon-180x180.png" />
      </head>
      <body className={`${geistSans.variable} ${geistMono.variable} antialiased`}>
        <NextIntlClientProvider locale={locale} messages={messages}>
          <GlobalErrorContent reset={reset} />
        </NextIntlClientProvider>
      </body>
    </html>
  );
};

export default GlobalErrorPage;
